import { observer } from "mobx-react";
import {
  useDispatch,
  useMessageStore,
  usePortfolioStore,
  useRootStore,
  useShareholdersStore,
  useTranslation,
} from "@/stores/RootStoreContext.ts";
import { useEffect, useMemo } from "react";
import {
  extractAggregatedShareholderDetailsUrl,
  prepareAndFilterContactRows,
} from "../contactsDomain.ts";
import { ShareholderDetailsState, toShareholderDetailsState } from "@/stores/ShareholdersStore.ts";
import { isOk, isPending } from "@/common/common.ts";
import {
  extractPortfolioContactsUrl,
  extractPortfolioShareholderUrl,
  PortfolioType,
  usePortfolio,
} from "@/portfolio/portfolioDomain.ts";
import {
  prepShareholderDetailsActions,
  prepShareholderDetailsDialogs,
  ShareholderDetailsContext,
  toShareholderDetailsTab,
} from "@/shareholderRegister/shareholders/shareholderDetailsDomain.ts";
import {
  ShareholderDetailsDialogs,
  ShareholderDetailsPageView,
} from "@/shareholderRegister/shareholders/ShareholderDetailsPage.tsx";
import { ShareholderCommitmentController } from "@/shareholderRegister/commitments/shareholderCommitmentsDomain.ts";
import {
  adminShareholdersShareholderCommitmentDetails,
  adminShareholdersShareholderCommitments,
  adminShareholdersShareholderContributionDetails,
  adminShareholdersShareholderContributions,
  adminShareholdersTransaction,
} from "@/utils/navigation.ts";
import { when } from "@/utils/functions.ts";
import {
  isContributionsEnabled,
  ShareholderContributionController,
} from "@/shareholderRegister/contributions/shareholderContributionsDomain.ts";

export const PortfolioShareholderPage = observer(function PortfolioShareholderPage({
  portfolioType,
  portfolioId,
  activeHeader,
  ...props
}: {
  shareholderId: string;
  portfolioType: PortfolioType;
  portfolioId?: string;
  activeHeader: string;
  activeTab: string;
}) {
  const location = useRootStore().location;
  const { t, f, lang } = useTranslation();
  const { apiService, http, userStore, contacts } = useRootStore();
  const alertStore = useMessageStore();
  const dispatch = useDispatch();
  const shareholdersStore = useShareholdersStore();
  const { shareholderDetailsStore, getShareholderDetails, messageStore } = shareholdersStore;
  const orgNo = shareholderDetailsStore.shareholderDetails?.organizationnumber ?? "";
  const { portfolio } = usePortfolio({ activeHeader, portfolioType, portfolioId });
  const shareholderId = Number(props.shareholderId);
  const portfolioStore = usePortfolioStore();
  const commitmentCtrl = useMemo(
    () =>
      new ShareholderCommitmentController(
        apiService,
        alertStore,
        orgNo,
        shareholderDetailsStore.shareholderDetails?.shareholderId === shareholderId
          ? shareholderDetailsStore.shareholderDetails?.commitments
          : undefined,
      ),
    [
      alertStore,
      apiService,
      orgNo,
      shareholderDetailsStore.shareholderDetails?.commitments,
      shareholderDetailsStore.shareholderDetails?.shareholderId,
      shareholderId,
    ],
  );
  const contributionCtrl = useMemo(
    () =>
      new ShareholderContributionController(
        apiService,
        alertStore,
        orgNo,
        shareholderDetailsStore.shareholderDetails?.shareholderId === shareholderId
          ? shareholderDetailsStore.shareholderDetails?.shareholderContributions
          : undefined,
      ),
    [
      alertStore,
      apiService,
      orgNo,
      shareholderDetailsStore.shareholderDetails?.shareholderContributions,
      shareholderDetailsStore.shareholderDetails?.shareholderId,
      shareholderId,
    ],
  );
  const response = isOk(portfolioStore.fetchPortfolioShareholdersResult)
    ? portfolioStore.fetchPortfolioShareholdersResult.data
    : undefined;
  const activeTab = toShareholderDetailsTab(props.activeTab);
  const shareholders = response?.shareholders ?? [];
  const companyItem = response?.companyItems.find(
    (c) => c.organisationNumber === orgNo,
  )?.companyName;
  const filteredRows = prepareAndFilterContactRows(
    portfolioStore.allCompanies,
    shareholders,
    portfolioStore.shareholderStatusFilter,
    portfolioStore.shareholderNameFilter.inputValue,
    portfolioStore.companyFilter?.value,
    portfolioStore.missingEmailModel.value,
  ).map((r) =>
    r.shareholderId !== shareholderId && r.companies.some((c) => c.shareholderId === shareholderId)
      ? { ...r, shareholderId: shareholderId }
      : r,
  );
  const filteredShareholders = shareholders.filter((s) =>
    filteredRows.some((r) => r.shareholderId === s.id),
  );
  const commitmentsEnabled =
    shareholderDetailsStore?.shareholderDetails?.featureToggles?.committedCapital;
  const contributionsEnabled = isContributionsEnabled(orgNo);
  const context: ShareholderDetailsContext = {
    shareholderId,
    orgNo,
    userRole: userStore.userRole,
    companyName: portfolioStore.allCompanies.find((x) => x.organisationNumber === orgNo)?.name,
    companies: filteredRows.find((r) => r.shareholderId === shareholderId)?.companies,
    commitmentsEnabled,
    commitments: commitmentCtrl.fetchShareholderCommitmentsResult,
    contributions: contributionCtrl.fetchShareholderContributionsResult,
    contributionsEnabled,
    canEdit: true,
    f,
    t,
    lang,
    activeTab,
  };
  const state: ShareholderDetailsState = toShareholderDetailsState({
    isLoading: !isOk(portfolioStore.fetchPortfolioShareholdersResult),
    store: shareholderDetailsStore,
    shareTypes: response?.shareTypes ?? [],
    allShareholders: shareholders,
    filteredShareholders: filteredShareholders,
    shareholderStatus: portfolioStore.shareholderStatusFilter,
    isDeleting: isPending(shareholdersStore.deleteShareholderResult),
  });
  const currentShareholder =
    contacts &&
    isOk(contacts.response) &&
    contacts.response.data.contacts.find((c) =>
      c.ownershipItems.some((o) => Number(o.id) === shareholderId),
    );
  const referrerUrl =
    currentShareholder && currentShareholder.ownershipItems.length > 1
      ? extractAggregatedShareholderDetailsUrl(portfolio, shareholderId)
      : extractPortfolioContactsUrl(portfolio);
  const actions = prepShareholderDetailsActions({
    activeTab,
    dispatch,
    lang,
    orgNo,
    setShareholderStatusFilter: portfolioStore.setShareholderStatusFilter,
    shareholderId,
    referrerUrl,
    shareholderUrl: (_, shareholderId, tab) =>
      extractPortfolioShareholderUrl(portfolio, shareholderId, tab),
    transactionDetailsUrl: adminShareholdersTransaction,
    commitmentsUrl: adminShareholdersShareholderCommitments,
    commitmentDetailsUrl: adminShareholdersShareholderCommitmentDetails,
    shareholderDetailsStore,
    commitmentCtrl,
    contributionCtrl,
    contributionsUrl: adminShareholdersShareholderContributions,
    contributionDetailsUrl: adminShareholdersShareholderContributionDetails,
    currentUrl: location.pathname,
    canEdit: true,
    editDetails: () =>
      portfolioStore.openEditShareholderDialog(lang, shareholderId, orgNo, portfolio),
    deleteShareholder: () =>
      shareholdersStore.deleteShareholder(
        shareholderId,
        when(
          extractPortfolioContactsUrl(portfolio),
          (referrerUrl) => () => dispatch([{ type: "navigate", url: referrerUrl }]),
        ),
      ),
    deleteRepresentative: (id) => shareholdersStore.deleteRepresentative(id),
    downloadShareholderCertificate: () =>
      shareholdersStore.downloadShareholderCertificate(shareholderId, lang),
    apiService,
    messageStore,
    http,
    user: userStore.user,
    companyName: companyItem,
  });

  useEffect(() => {
    getShareholderDetails(shareholderId);
    return () => {
      shareholderDetailsStore.resetShareholder();
    };
  }, [getShareholderDetails, shareholderDetailsStore, shareholderId]);

  useEffect(() => {
    if (!portfolioStore.fetchPortfolioShareholdersResult) {
      portfolioStore.fetchPortfolioShareholders(portfolio);
    }
  }, [portfolio, portfolioStore]);

  useEffect(() => {
    if (
      orgNo &&
      ((activeTab === "commitments" && !commitmentsEnabled) ||
        (activeTab === "contributions" && !contributionsEnabled))
    ) {
      dispatch([
        {
          type: "navigate",
          url: extractPortfolioShareholderUrl(portfolio, shareholderId, "overview"),
          replace: true,
        },
      ]);
    }
  }, [
    activeTab,
    commitmentsEnabled,
    contributionsEnabled,
    dispatch,
    orgNo,
    portfolio,
    shareholderId,
  ]);

  return (
    <>
      <ShareholderDetailsPageView
        alertStore={messageStore}
        context={context}
        state={state}
        actions={actions}
      />
      <ShareholderDetailsDialogs
        {...prepShareholderDetailsDialogs(
          shareholdersStore,
          commitmentCtrl,
          contributionCtrl,
          context,
          state,
        )}
      />
    </>
  );
});
